import { StatusBadge } from "@/components/StatusBadge";
import { Notify } from "@/components/ui/notify";
import type { MachineDetail } from "@/features/lines/hooks";
import { useParameters } from "@/features/line-editor/hooks";
import { useMachineAlarms } from "../hooks";
import { formatRelativeTime, getMachineStatusFriendlyLabel } from "@/lib/display-labels";
import { StatusEntryBlock } from "../StatusEntryBlock";
import { ManualEntriesBlock } from "./ManualEntriesBlock";
import { ProductionEntryBlock } from "./ProductionEntryBlock";
import { ScrapEntryBlock } from "./ScrapEntryBlock";
import { MachineIndicatorsBlock } from "./MachineIndicatorsBlock";

interface Props {
  machine: MachineDetail;
  lineId: string;
}

export function OperationTab({ machine, lineId }: Props) {
  const alarmsQuery = useMachineAlarms(machine.id);
  const { data: alarms = [] } = alarmsQuery;
  const parametersQuery = useParameters(machine.id);
  const { data: parameters = [] } = parametersQuery;
  const activeAlarms = alarms.filter(
    (a) => a.state === "UNACKNOWLEDGED" || a.state === "ACKNOWLEDGED"
  );
  const manualStatusParam = parameters.find(
    (p) => p.category === "STATUS" && p.source === "MANUAL"
  );
  // Contadores de produção ganham bloco próprio (saem do registro manual).
  const productionParams = parameters.filter((p) => p.productionRole === "PRODUCES");
  // Máquina-contador também reporta refugo (caminho único do modo por-máquina).
  const isProductionCounter = productionParams.length > 0;
  // Spec 081 — quem RECEBE refugo é a máquina com contador `PRODUCES` ou `SCRAPS`, que é a mesma
  // lista aceita por `createLoss`. Gatear o bloco só em `PRODUCES` escondia o único caminho de
  // refugo de uma máquina que conta exatamente isso.
  const acceptsScrap = parameters.some(
    (p) => p.productionRole === "PRODUCES" || p.productionRole === "SCRAPS"
  );
  // Spec 081 (FR-018b) — a máquina MEDE alguma coisa (tem contador com sentido) mas nenhum deles
  // PRODUZ. É um estado legítimo e silencioso: não há onde apontar produção nesta máquina, e sem
  // este aviso a aba simplesmente não mostra o bloco, sem dizer por quê.
  const measuresSomething = parameters.some((p) => p.productionRole != null);
  const measuresButNeverProduces = measuresSomething && !isProductionCounter;

  const sensorLinks = (machine.parameters ?? []).flatMap((p) => p.sensorLinks ?? []);
  const sensorStats = sensorLinks.reduce(
    (acc, l) => {
      const s = l.sensor.status;
      if (s === "ONLINE") acc.online++;
      else if (s === "INCONSISTENT") acc.inconsistent++;
      else acc.offline++;
      return acc;
    },
    { online: 0, inconsistent: 0, offline: 0 }
  );

  return (
    <div className="space-y-4 text-sm">
      {/* Em erro, `parameters` cairia para [] e os blocos de registro (Status/
          Produção/Refugo) sumiriam da tela sem aviso, bloqueando o operador sem explicar
          por quê. Substitui os blocos por um estado de erro explícito com retry. */}
      {parametersQuery.isError ? (
        <Notify.QueryError
          error={parametersQuery.error}
          onRetry={() => void parametersQuery.refetch()}
          title="Não foi possível carregar os parâmetros de registro"
        />
      ) : (
        <>
          {manualStatusParam ? (
            <StatusEntryBlock machine={machine} lineId={lineId} statusParam={manualStatusParam} />
          ) : null}
          {productionParams.map((p) => (
            <ProductionEntryBlock key={p.id} parameter={p} machineId={machine.id} lineId={lineId} />
          ))}
          {measuresButNeverProduces ? (
            <Notify.Banner
              variant="info"
              title="Esta máquina não aponta produção"
              description="Os contadores desta máquina medem consumo ou refugo. Para registrar produção aqui, cadastre um contador com o sentido Produz na aba Parâmetros."
            />
          ) : null}
          {acceptsScrap ? (
            <ScrapEntryBlock machineId={machine.id} machineName={machine.name} lineId={lineId} />
          ) : null}
          <ManualEntriesBlock machineId={machine.id} lineId={lineId} parameters={parameters} />
        </>
      )}
      <MachineIndicatorsBlock machineId={machine.id} lineId={lineId} />
      <section>
        <h3 className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
          Estado atual
        </h3>
        <div className="flex items-center gap-3">
          <StatusBadge status={machine.status} kind="machine" />
          <span className="text-xs text-muted-foreground">
            {machine.statusSince
              ? `${getMachineStatusFriendlyLabel(machine.status)} desde ${formatRelativeTime(machine.statusSince)}`
              : "sem registro de transição"}
          </span>
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
          Resumo operacional
        </h3>
        {/* "Alarmes ativos" é dado operacional crítico; em erro não pode virar
            0 falso. Substitui o resumo inteiro (as demais células dependem do mesmo bloco). */}
        {alarmsQuery.isError ? (
          <Notify.QueryError
            variant="inline"
            error={alarmsQuery.error}
            onRetry={() => void alarmsQuery.refetch()}
            title="Não foi possível carregar o resumo operacional"
          />
        ) : (
          <dl className="grid grid-cols-2 gap-2 rounded-md border bg-muted/20 p-3 text-xs">
            <div>
              <dt className="text-muted-foreground">Alarmes ativos</dt>
              <dd className="font-mono text-sm font-semibold">{activeAlarms.length}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Sensores online</dt>
              <dd className="font-mono text-sm font-semibold">{sensorStats.online}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Inconsistentes</dt>
              <dd className="font-mono text-sm font-semibold">{sensorStats.inconsistent}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Offline/sem leitura</dt>
              <dd className="font-mono text-sm font-semibold">{sensorStats.offline}</dd>
            </div>
          </dl>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
          Parâmetros e valores
        </h3>
        {!machine.parameters || machine.parameters.length === 0 ? (
          <p className="text-xs text-muted-foreground">Sem parâmetros configurados.</p>
        ) : (
          <ul className="space-y-1">
            {machine.parameters.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded border px-2 py-1.5"
              >
                <span>{p.name}</span>
                <span className="font-mono text-xs">
                  {p.currentValue ?? "—"} {p.unit ?? ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-[10px] italic text-muted-foreground">
        Resumo operacional — leitura apenas. Atualizado por eventos em tempo real.
      </p>
    </div>
  );
}
